import { useCallback, useEffect, useState } from 'react'

import { FileText, Xmark } from '@gravity-ui/icons'
import { Button, Modal, Spinner } from '@heroui/react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { DuplicateUploadModal } from '@/components/common/DuplicateUploadModal'
import { useWarehouseStore } from '@/store/warehouse.store'
import type { DuplicateFileWarning } from '@/types/uploads'
import type { WarehouseImportResult } from '@/types/warehouse'

interface WarehouseImportDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess?: () => void;
}

export function WarehouseImportDialog({ isOpen, onOpenChange, onSuccess }: WarehouseImportDialogProps) {
  const { t } = useTranslation()
  const { importArticles, isLoading } = useWarehouseStore()
  const [file, setFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<WarehouseImportResult | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateFileWarning | null>(null)

  // Reset state when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setFile(null)
      setImportResult(null)
      setDuplicateWarning(null)
    }
  }, [isOpen])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
      setDuplicateWarning(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  })

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleUpload = useCallback(async (forceUpload = false) => {
    if (!file) return

    try {
      const formData = new FormData()
      formData.append('file', file)

      const result = await importArticles(formData, forceUpload)
      setImportResult(result)
      setDuplicateWarning(null)
      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      if (error?.status === 409 && error?.data) {
        setDuplicateWarning(error.data as DuplicateFileWarning)
      } else {
        console.error('Warehouse import failed:', error)
      }
    }
  }, [file, importArticles, onSuccess])

  const handleClose = useCallback(() => {
    setFile(null)
    setImportResult(null)
    setDuplicateWarning(null)
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop variant="blur" className="bg-zinc-950/40" isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className="font-bold">
                {importResult !== null
                  ? t('warehouse.import_dialog.success_title', 'Импорт успешно завершен')
                  : t('warehouse.import_dialog.title', 'Импорт деталей из XLSX')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className="mt-2 space-y-4">
              {importResult !== null ? (
                <div className="space-y-4">
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 bg-zinc-50/30 dark:bg-zinc-900/10 flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
                      <FileText width={20} height={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate text-zinc-800 dark:text-zinc-200">
                        {file?.name}
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-500">
                        {file ? formatBytes(file.size) : ''}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col items-center justify-center p-5 rounded-2xl border border-success-200/50 dark:border-success-900/20 bg-success-50/10 dark:bg-success-950/5 text-center">
                      <span className="text-3xl font-bold text-success">
                        {importResult.Total}
                      </span>
                      <span className="text-xs text-success/80 mt-2 uppercase tracking-wider font-semibold">
                        {t('warehouse.import_dialog.total_added', 'Добавлено деталей')}
                      </span>
                    </div>

                    <div className="flex flex-col items-center justify-center p-5 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/30 dark:bg-zinc-900/10 text-center">
                      <span className="text-3xl font-bold text-zinc-600 dark:text-zinc-400">
                        {importResult.Skipped}
                      </span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-2 uppercase tracking-wider font-semibold">
                        {t('warehouse.import_dialog.skipped_rows', 'Пропущено строк')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : file ? (
                <div className="space-y-3">
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 bg-zinc-50/30 dark:bg-zinc-900/10 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
                        <FileText width={20} height={20} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate text-zinc-800 dark:text-zinc-200">
                          {file.name}
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-500">
                          {formatBytes(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      onClick={() => setFile(null)}
                      aria-label="Remove file"
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                      isDisabled={isLoading}
                    >
                      <Xmark width={16} height={16} />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 min-h-[200px] ${
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600 bg-zinc-50/50 dark:bg-zinc-900/30'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div
                      className={`p-4 rounded-full ${
                        isDragActive
                          ? 'bg-primary/10 text-primary'
                          : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'
                      }`}
                    >
                      <FileText width={28} height={28} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                        {isDragActive
                          ? t('warehouse.import_dialog.drag_active', 'Отпустите XLSX файл здесь...')
                          : t('warehouse.import_dialog.description', 'Перетащите XLSX файл сюда или нажмите для выбора файла')}
                      </p>
                      <p className="text-xs text-zinc-400 dark:text-zinc-500">
                        {t('warehouse.import_dialog.format_hint', 'Поддерживаются файлы в формате .xlsx')}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" className="mt-2" type="button" onClick={open}>
                      {t('warehouse.import_dialog.select_file', 'Выбрать файл')}
                    </Button>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="flex gap-3 justify-end mt-6">
              {importResult !== null ? (
                <Button variant="secondary" onClick={handleClose}>
                  {t('warehouse.import_dialog.done', 'Готово')}
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={handleClose} isDisabled={isLoading}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleUpload()}
                    isDisabled={!file || isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" color="current" />
                        <span>{t('warehouse.import_dialog.uploading', 'Загрузка и обработка...')}</span>
                      </div>
                    ) : (
                      t('warehouse.import_dialog.upload', 'Загрузить')
                    )}
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>

    <DuplicateUploadModal
      isOpen={Boolean(duplicateWarning)}
      warningData={duplicateWarning}
      isUploading={isLoading}
      onConfirm={() => handleUpload(true)}
      onCancel={() => setDuplicateWarning(null)}
    />
    </>
  )
}
